import React, { useEffect, useMemo, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Base64 } from 'js-base64';

import { FunctionalRootState, getPreferenceFileUrlFromAccount, Toaster, UrlUtils } from '@share/utils';
import { handleGoogleLogin, ILoginState, login, setLoginRedirect } from '@share/store/slices';
import { Routes } from '@share/constants';

import AshevillePlayMain from '@assets/images/ashevilleplay/asheville-hero.jpg';

import './style.scss';
import GoogleButton from '../google-auth/googleAuth';
import { BACKGROUND_PHOTO_FIELD_KEY, IAccount } from '@share/common-types';
import { ProcessData } from '../../../pages/app/utils';

const REDIRECT_QUERY_PARAM = 'redirect';
const SEARCH_QUERY_PARAM = 'search';

export function LoginPageAshevillePlay() {

  const loginStore: ILoginState = useSelector((state: FunctionalRootState) => state.loginStore);

  const { account, loading } = loginStore;

  const history = useHistory();

  const dispatch = useDispatch();

  const redirect = useMemo(() => UrlUtils.getSearchParam(REDIRECT_QUERY_PARAM), []);
  const search = useMemo(() => UrlUtils.getSearchParam(SEARCH_QUERY_PARAM), []);

  useEffect(() => {
    if (redirect) {
      dispatch(setLoginRedirect(`${redirect}${search ? `?${Base64.decode(search)}` : ''}`));
    }
  }, [redirect]);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  
  const [errors, setErrors] = useState<{
    email?: string;
    password?: string;
    general?: string;
  }>({});
    
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };
  
  const validateForm = () => {
    const newErrors: typeof errors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
        
    dispatch(login(formData.email, formData.password, undefined, () => ProcessData(dispatch)));
  };
    
  const googleSignUp = async (credentialResponse: any) => {
    try {
      if (credentialResponse?.credential) {
        dispatch(handleGoogleLogin(credentialResponse.credential, () => ProcessData(dispatch)));
      } else {
        Toaster.error('Google sign-up failed. Please try again.');
      }
    } catch (error) {
      console.error('Google sign-up error:', error);
      Toaster.error('Google sign-up failed. Please try again.');
    }
  };

  const onGoogleSignUpFailed = (error: any) => {
    console.error('Google sign-up failed:', error);
    Toaster.error('Google sign-up failed. Please try again.');
  };

  const logoDB = account?.ashevillePlayGoogleLogo;

  const onNavigateToSignUp = () => {
    history.push(`/${account?.name}${Routes.SignUp}`);
  };

  const onNavigateToHome = () => {
    history.push(`/${account?.name}${Routes.Search}`);
  };

  const backgroundImage = getPreferenceFileUrlFromAccount(account as IAccount, BACKGROUND_PHOTO_FIELD_KEY);
  const image = backgroundImage ? backgroundImage : AshevillePlayMain;

  return (
    <div className="login-page-asheville-play" style={{ backgroundImage: `linear-gradient(135deg, rgba(26, 26, 26, 0.4) 0%,rgba(26, 26, 26, 0.2) 100%), url(${image})` }}>
      <div className="logo-top-left" onClick={onNavigateToHome}>
        <img src={logoDB} alt="Asheville Play" />
      </div>

      <div className="login-card">
        <div className="login-card-header">
          <div className="logo-container">
            <h1 className="logo-text">Asheville Play</h1>
            <p className="logo-subtitle">Your Asheville starts here</p>
          </div>
          <h3>Sign In</h3>
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <form onSubmit={handleSubmit}>
            <div className="input-group">
              <label className="input-label" htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                className={`input-email-field ${errors.email ? 'error' : ''}`}
              />
              {errors.email && <span className="input-error">{errors.email}</span>}
            </div>
            
            <div className="input-group">
              <label className="input-label" htmlFor="password">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                className={`input-password-field ${errors.password ? 'error' : ''}`}
              />
              {errors.password && <span className="input-error">{errors.password}</span>}
            </div>
            
            {errors.general && (
              <span className="input-error" style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
                {errors.general}
              </span>
            )}
            
            <div className="remember-me-container">
              <label>
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                />
                <span>Remember me</span>
              </label>
              <button className="forgot-password-link" type="button" onClick={() => history.push(`/${account?.name}${Routes.ForgotPassword}`)}>
                Forgot Password?
              </button>
            </div>
            
            <button className="full-width-button" type="submit" disabled={loading}>
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>
          
          <div className="divider-login">
            <span className="divider-text">Or continue with</span>
          </div>
          
          <GoogleButton
            extraClassName="commonBlueBtn"
            onSuccess={googleSignUp}
            onFailed={onGoogleSignUpFailed}
          />
        </div>

        <div className="signup-section">
          <p className="signup-text">Don't have an account?</p>
          <button className="text-button"
            type="button" 
            onClick={onNavigateToSignUp}
            style={{ fontSize: '1rem' }}
          >
            Sign up
          </button>
        </div>
      </div>
    </div>
  );
};
